import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackButton from "../components/BackButton";
import { apiRequest } from "../api/client";

const PAGE_MODULE = "data_pricing_transparency";

function normalizeText(v) {
  if (v === null || v === undefined) return "";
  return String(v).replace(/\r\n/g, "\n").trim();
}

function isHttpUrl(url) {
  if (!url) return false;
  const s = String(url);
  return s.startsWith("http://") || s.startsWith("https://");
}

function SectionIcon({ title }) {
  const key = String(title || "").toLowerCase();

  if (key.includes("retailer")) {
    return (
      <svg viewBox="0 0 24 24" className="h-8 w-8 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M3 7l9-4 9 4" />
        <path d="M4 7v13h16V7" />
        <path d="M8 20v-7h8v7" />
      </svg>
    );
  }
  if (key.includes("promotion") || key.includes("offer")) {
    return (
      <svg viewBox="0 0 24 24" className="h-8 w-8 text-sky-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M20.59 13.41L11 3.83a2 2 0 00-1.41-.59H4a1 1 0 00-1 1v5.59a2 2 0 00.59 1.41l9.59 9.59a2 2 0 002.82 0l4.59-4.59a2 2 0 000-2.83z" />
        <path d="M7.5 7.5h0" />
      </svg>
    );
  }
  if (key.includes("update")) {
    return (
      <svg viewBox="0 0 24 24" className="h-8 w-8 text-amber-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M21 12a9 9 0 11-3.27-6.93" />
        <path d="M21 3v7h-7" />
      </svg>
    );
  }
  if (key.includes("quality") || key.includes("check")) {
    return (
      <svg viewBox="0 0 24 24" className="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  }
  if (key.includes("base")) {
    return (
      <svg viewBox="0 0 24 24" className="h-8 w-8 text-sky-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M20.59 13.41L11 3.83a2 2 0 00-1.41-.59H4a1 1 0 00-1 1v5.59a2 2 0 00.59 1.41l9.59 9.59a2 2 0 002.82 0l4.59-4.59a2 2 0 000-2.83z" />
        <path d="M7.5 7.5h0" />
      </svg>
    );
  }
  if (key.includes("loyalty")) {
    return (
      <svg viewBox="0 0 24 24" className="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4z" />
        <path d="M8 12h8" />
        <path d="M12 8v8" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="h-8 w-8 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 22a10 10 0 100-20 10 10 0 000 20z" />
      <path d="M12 8h.01" />
      <path d="M11 12h1v6h-1z" />
    </svg>
  );
}

function InfoRow({ icon, title, description, className = "" }) {
  return (
    <div className={["rounded-2xl border-2 border-[#E6EAF0] bg-white p-5 shadow-sm", className].join(" ")}>
      <div className="flex flex-col items-center gap-3">
        <div className="mt-0.5 flex h-16 w-16 items-center justify-center">
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="text-[18px] font-semibold text-[#24303A] text-center">{title}</h3>
          <p className="mt-1 whitespace-pre-line text-[16px] leading-5 text-[#6E7B86] text-center">{description}</p>
        </div>
      </div>
    </div>
  );
}

export default function DataPricingTransparency({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageHeader, setPageHeader] = useState(null);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      const res = await apiRequest(
        `api/page_sections?page_module=${encodeURIComponent(PAGE_MODULE)}`
      );
      if (!alive) return;
      if (!res.ok) {
        setError("Failed to load transparency content.");
        setPageHeader(null);
        setSections([]);
      } else {
        const firstHeader = Array.isArray(res.data?.page_sections) ? res.data.page_sections[0] : null;
        setPageHeader(firstHeader || null);
        setSections(Array.isArray(res.data?.grouped_sections) ? res.data.grouped_sections : []);
      }
      setLoading(false);
    })().catch(() => {
      if (!alive) return;
      setError("Failed to load transparency content.");
      setPageHeader(null);
      setSections([]);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const dataSourceItems = useMemo(() => {
    const group = sections.find((s) => s?.section_type === "data_pricing_transparency_data");
    return Array.isArray(group?.items) ? group.items : [];
  }, [sections]);

  const savingsItems = useMemo(() => {
    const group = sections.find((s) => s?.section_type === "calulate_price_savings_data");
    return Array.isArray(group?.items) ? group.items : [];
  }, [sections]);

  const lastUpdatedText = useMemo(() => {
    try {
      return new Date().toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }, []);

  const heroTitle = normalizeText(pageHeader?.title) || "Data & Pricing Transparency";
  const heroSubTitle = normalizeText(pageHeader?.sub_title) || "Clear prices.\nSmarter savings.";
  const heroDescription =
    normalizeText(pageHeader?.description) ||
    "We show you how prices are built, where data comes from, and how you save—so you always know you're getting the best value.";

  return (
    <div className="min-h-screen text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-6 py-6">
        <div className="mb-4">
          <BackButton to="/" label="Back" />
        </div>

        <section className="bg-white overflow-hidden">
          <div className="grid gap-8 py-6 md:grid-cols-[1.1fr_0.9fr]">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[12px] font-semibold tracking-wide text-[#6E7B86]">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </span>
                <span className="uppercase text-[14px]">{heroTitle}</span>
              </div>

              <h1 className="mt-4 text-[28px] font-semibold leading-[1.05] tracking-tight text-[#24303A] md:text-[28px] whitespace-pre-line">
                {heroSubTitle}
              </h1>

              <p className="mt-4 max-w-xl text-[16px] leading-6 text-[#6E7B86]">
                {heroDescription}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/features")}
                  className="rounded-full bg-[#7352C7] px-6 py-2 text-[16px] font-bold text-white shadow-sm"
                >
                  Compare Prices
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/faq")}
                  className="rounded-full border-2 bg-[#7352C71A] px-6 py-2 text-[16px] font-semibold text-[#7352C7] shadow-sm border-[#7352C7]"
                >
                  How It Works
                </button>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-end">
              <div className="relative w-full max-w-[360px] rounded-3xl border border-[#E6EAF0] bg-gradient-to-b from-[#F5FAFF] to-[#F6F1FF] p-6">

                <div className="relative mx-auto flex flex-col items-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-white shadow-sm ring-1 ring-[#E6EAF0]">
                    <svg viewBox="0 0 24 24" className="h-10 w-10 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#E6EAF0] bg-white py-10">
            <h2 className="text-center text-[20px] font-semibold text-[#24303A]">Where our data comes from</h2>

            <div className="mt-6 grid gap-8 grid-cols-4">
              {loading ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="h-[132px] rounded-2xl border border-[#E6EAF0] bg-white/60" />
                ))
              ) : dataSourceItems.length ? (
                dataSourceItems.map((it) => (
                  <InfoRow
                    key={it?.id ?? it?.title}
                    icon={
                      isHttpUrl(it?.image_url) ? (
                        <img
                          src={it.image_url}
                          alt=""
                          className="h-16 w-16 object-contain"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <SectionIcon title={it?.title} />
                      )
                    }
                    title={normalizeText(it?.title)}
                    description={normalizeText(it?.description)}
                  />
                ))
              ) : (
                <p className="col-span-full text-center text-[16px] text-[#6E7B86]">
                  {error ? error : "No data sources have been configured yet."}
                </p>
              )}
            </div>

            <div className="mt-14">
              <h2 className="text-center text-[20px] font-semibold text-[#24303A]">How we calculate prices &amp; savings</h2>
              <p className="mt-2 text-center text-[16px] leading-6text-[#6E7B86]">We compare multiple inputs to find the best value for you.</p>

              <div className="mt-6 grid gap-12 md:grid-cols-3 h-[200px]">
                {loading ? (
                  Array.from({ length: 3 }).map((_, idx) => (
                    <div key={idx} className="h-[200px] rounded-2xl border-2 border-[#E6EAF0] bg-white" />
                  ))
                ) : savingsItems.length ? (
                  savingsItems.map((it, idx) => (
                    <InfoRow
                      key={it?.id ?? it?.title}
                      icon={
                        isHttpUrl(it?.image_url) ? (
                          <img
                            src={it.image_url}
                            alt=""
                            className="h-16 w-16 object-cover"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <SectionIcon title={it?.title} />
                        )
                      }
                      title={normalizeText(it?.title)}
                      description={normalizeText(it?.description)}
                      className={
                        idx === 0
                          ? "bg-[#F2F8FF] border-[#2EB5C980]"
                          : idx === 1
                            ? "bg-[#F59E0B0D] border-[#F59E0B80]"
                            : "bg-[#2FC98D1A] border-[#2FC98D80]"
                      }
                    />
                  ))
                ) : (
                  <p className="col-span-full text-center text-[16px] text-[#6E7B86]">
                    {error ? error : "No calculation inputs have been configured yet."}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-20 space-y-4">
              <div className="rounded-2xl border border-[#E6EAF0] bg-[#F5F2FF] p-5">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-10 w-10 items-center justify-center text-[#7352C7] font-bold">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 18h.01" />
                      <path d="M11 9h1v6h-1z" />
                      <path d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[18px] font-bold text-[#7352C7]">Please note</h3>
                    <ul className="mt-2 space-y-1 text-[16px] leading-5 text-[#6E7B86]">
                      <li className="flex gap-2">
                        <span className="mt-0.5 text-[#7352C7] font-bold">✓</span>
                        <span className="text-[#24303A]">Not every product or retailer supports every price view at all times.</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-0.5 text-[#7352C7] font-bold">✓</span>
                        <span className="text-[#24303A]">Prices and offers are subject to change by retailers.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[#E6EAF0] bg-[#F5F2FF] p-5">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-10 w-10 items-center justify-center text-[#7352C7] font-bold">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 8v4l3 3" />
                      <path d="M12 22a10 10 0 100-20 10 10 0 000 20z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[18px] font-medium text-[#24303A]">Last updated: {lastUpdatedText}</h3>
                    <p className="mt-1 text-[16px] leading-5 text-[#24303A]">
                      We refresh data frequently so you can shop with confidence.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

